import { WSDataItemProps, WSGrid } from "@wingspanhq/fe-component-library";
import { IPayeeResponse, TinType } from "@wingspanhq/payments/dist/interfaces";
import { wsName, WSNameParams } from "@wingspanhq/utils/dist/name/wsName";
import { useMemo } from "react";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { DataItem } from "../../../shared/components/DataItem";
import { selectorCountryName } from "../../../shared/selectors/selectorCountryName";
import { selectorTaxClassificationLabel } from "../../../shared/selectors/selectorTaxClassificationLabel";
import { getLast4 } from "../../../shared/utils/getLast4";
import {
  getTaxIdLabel,
  getTaxIdVerificationAlert
} from "../../../shared/utils/taxId";
import { isPayeeProvidedInfo } from "../routes/RoutePayeeDetails/utils";
import { getPayeeNames } from "../selectors/getPayeeNames";
import { selectorPayeeDefaultTinType } from "../selectors/selectorPayeeDefaultTinType";
import { useEditEmail } from "./EditEmail/useEditEmail";

type Props = {
  payee: IPayeeResponse;
  editable?: boolean;
};

export const ContractorInformation: React.FC<Props> = ({ payee, editable }) => {
  const editEmail = useEditEmail();
  const featureFlags = useFeatureFlags();

  const fields = useMemo(() => {
    if (isPayeeProvidedInfo(payee)) {
      const names = wsName({
        user: payee.user,
        member: payee.member
      } as WSNameParams);

      const payeeW9Data = payee.payeeOwnedData?.payeeW9Data;
      const tinType = selectorPayeeDefaultTinType(payee);

      const country = payeeW9Data?.country || "US";
      const taxIdLabel = getTaxIdLabel(tinType, country);

      const result: WSDataItemProps[] = [
        {
          label: "Name",
          value: names.getResolvedName()
        },
        ...(tinType === TinType.Business && payeeW9Data?.disregardedEntityName
          ? [
              {
                label: "Disregarded entity name",
                value: payeeW9Data?.disregardedEntityName
              }
            ]
          : []),
        {
          label: "Country of residence",
          value: payeeW9Data?.country
            ? selectorCountryName(payeeW9Data?.country)
            : undefined
        },
        {
          label: "Federal tax classification",
          value:
            tinType === "Individual"
              ? "Sole proprietorship / Single member LLC"
              : payeeW9Data?.companyStructure
              ? selectorTaxClassificationLabel(payeeW9Data?.companyStructure)
              : undefined
        },
        {
          label: taxIdLabel,
          value:
            tinType === "Individual"
              ? payeeW9Data?.ssn
                ? "•••••" + getLast4(payeeW9Data?.ssn)
                : undefined
              : payeeW9Data?.ein
              ? "•••••" + getLast4(payeeW9Data?.ein)
              : undefined,
          alert: getTaxIdVerificationAlert({
            country,
            status: payeeW9Data?.payeeTinVerification?.status,
            isPendingPaused: featureFlags.data?.is1099FilingPaused
          })
        },
        {
          label: "Email",
          value: names.email
        },
        {
          label: "Address",
          value:
            [
              payeeW9Data?.addressLine1,
              payeeW9Data?.addressLine2,
              [
                payeeW9Data?.city,
                payeeW9Data?.state,
                payeeW9Data?.postalCode
              ].join(" ")
            ]
              .filter(Boolean)
              .join("\n")
              .trim() || undefined
        }
      ];

      return result;
    } else {
      const names = getPayeeNames(payee);
      const payerOwnedData = payee.payerOwnedData;
      const payeeW9Data = payerOwnedData.payeeW9Data;

      const tinType = selectorPayeeDefaultTinType(payee);
      const country = payeeW9Data.country || "US";
      const taxIdLabel = getTaxIdLabel(tinType, country);

      const name =
        (tinType === "Individual"
          ? [payeeW9Data.firstName, payeeW9Data.lastName]
              .filter(Boolean)
              .join(" ") || payerOwnedData.payeeName
          : payeeW9Data.legalBusinessName || payerOwnedData.payeeCompanyName) ||
        names.getResolvedName();

      const result: WSDataItemProps[] = [
        {
          label: "Name",
          value: name !== names.email ? name : undefined
        },
        ...(tinType === TinType.Business && payeeW9Data.disregardedEntityName
          ? [
              {
                label: "Disregarded entity name",
                value: payeeW9Data.disregardedEntityName
              }
            ]
          : []),
        {
          label: "Country of residence",
          value: country ? selectorCountryName(country) : undefined
        },
        {
          label: "Federal tax classification",
          value:
            tinType === "Individual"
              ? "Sole proprietorship / Single member LLC"
              : payeeW9Data.companyStructure
              ? selectorTaxClassificationLabel(payeeW9Data.companyStructure)
              : undefined
        },
        {
          label: taxIdLabel,
          value:
            tinType === "Individual"
              ? payeeW9Data.ssn
                ? "•••••" + getLast4(payeeW9Data.ssn)
                : undefined
              : payeeW9Data.ein
              ? "•••••" + getLast4(payeeW9Data.ein)
              : undefined,
          alert: getTaxIdVerificationAlert({
            country,
            status: payeeW9Data?.payeeTinVerification?.status,
            isPendingPaused: featureFlags.data?.is1099FilingPaused
          })
        },
        {
          label: {
            text: "Email",
            action:
              editable &&
              payee.payerCapabilities?.canUpdatePayeeEmail &&
              featureFlags.data?.editContractorEmail
                ? {
                    label: "Edit",
                    onClick: () => {
                      editEmail.run({
                        email: names.email,
                        payeeId: payee.payeeId
                      });
                    }
                  }
                : undefined
          },
          value: names.email
        },
        {
          label: "Address",
          value:
            [
              payeeW9Data.addressLine1,
              payeeW9Data.addressLine2,
              [
                payeeW9Data.city,
                payeeW9Data.state,
                payeeW9Data.postalCode
              ].join(" ")
            ]
              .filter(Boolean)
              .join("\n")
              .trim() || undefined
        }
      ];

      return result;
    }
  }, [payee]);

  return (
    <WSGrid gutter="2XL">
      {fields.map((props, index) => (
        <WSGrid.Item
          key={`${props.label} + ${index}`}
          span={{ xs: "12", s: "6" }}
        >
          <DataItem {...props} />
        </WSGrid.Item>
      ))}
    </WSGrid>
  );
};
